import { buildQueryString, fetchWrapper } from '@/_helpers';

export const callCenterApi = {
    // Labels
    getLabels: ({ team: teamId, page = 1, limit = 50, sortBy = 'createdAt:desc' } = {}) => {
        const queryString = buildQueryString({
            team: teamId,
            page,
            limit,
            sortBy,
        });
        return fetchWrapper.get(`call-center/labels?${queryString}`);
    },
    createLabel: (data) => fetchWrapper.post('call-center/labels', data),
    updateLabel: (labelId, data) => fetchWrapper.patch(`call-center/labels/${labelId}`, data),
    deleteLabel: (labelId) => fetchWrapper.delete(`call-center/labels/${labelId}`),

    // Presets
    getPresets: ({ team: teamId, teams, page = 1, limit = 50, sortBy = 'createdAt:desc' } = {}) => {
        const queryString = buildQueryString({
            team: teamId,
            teams,
            page,
            limit,
            sortBy,
        });
        return fetchWrapper.get(`call-center/presets?${queryString}`);
    },
    createPreset: (data) => fetchWrapper.post('call-center/presets', data),
    updatePreset: (presetId, data) => fetchWrapper.patch(`call-center/presets/${presetId}`, data),
    deletePreset: (presetId) => fetchWrapper.delete(`call-center/presets/${presetId}`),

    // Campaigns
    getCampaigns: ({ team: teamId, teams, page = 1, limit = 50, sortBy = 'createdAt:desc', archived } = {}) => {
        const queryString = buildQueryString({
            team: teamId,
            teams,
            page,
            limit,
            sortBy,
            archived,
        });
        return fetchWrapper.get(`call-center/campaigns?${queryString}`);
    },
    createCampaign: (data) => fetchWrapper.post('call-center/campaigns', data),
    updateCampaign: (id, data) => fetchWrapper.patch(`call-center/campaigns/${id}`, data),
    deleteCampaign: (id) => fetchWrapper.delete(`call-center/campaigns/${id}`),
    startCampaign: (id) => fetchWrapper.post(`call-center/campaigns/${id}/queue/start`),
    pauseCampaign: (id) => fetchWrapper.post(`call-center/campaigns/${id}/queue/pause`),
    resumeCampaign: (id) => fetchWrapper.post(`call-center/campaigns/${id}/queue/resume`),
    stopCampaign: (id) => fetchWrapper.post(`call-center/campaigns/${id}/queue/stop`),
    getCampaignQueueStatus: (id) => fetchWrapper.get(`call-center/campaigns/${id}/queue/status`),
    getQueues: ({ team: teamId, teams } = {}) => {
        const queryString = buildQueryString({
            team: teamId,
            teams,
        });
        return fetchWrapper.get(`call-center/campaigns/queues?${queryString}`);
    },
    getCampaignConfig: (id) => fetchWrapper.get(`call-center/campaigns/${id}/config`),
    updateCampaignConfig: (id, data) => fetchWrapper.put(`call-center/campaigns/${id}/config`, data),

    // Contacts
    getContacts: ({ team, limit, teams, status } = {}) =>
        fetchWrapper.get(
            `call-center/contacts?limit=${limit || 10}&${team ? `team=${team}` : ''}${teams ? `&teams=${teams}` : ''}${status ? `&status=${status}` : ''}`
        ),

    // Calls
    getCalls: ({
        team: teamId,
        teams,
        page = 1,
        limit = 50,
        sortBy = 'createdAt:desc',
        status,
        label,
        contact,
        preset,
        active,
        startDate,
        endDate,
        callId,
    } = {}) => {
        const queryString = buildQueryString({
            team: teamId,
            page,
            limit,
            sortBy,
            status,
            label,
            contact,
            preset,
            active,
            startDate,
            endDate,
            callId,
        });
        return fetchWrapper.get(`call-center/calls?${queryString}${teams ? `&teams=${teams}` : ''}`);
    },
    getActiveCalls: ({ team: teamId } = {}) => fetchWrapper.get(`call-center/calls/active?team=${teamId}`),
    createCall: (data) => fetchWrapper.post('call-center/calls', data),
    getCall: (callId) => fetchWrapper.get(`call-center/calls/${callId}`),
    updateCall: (callId, data) => fetchWrapper.patch(`call-center/calls/${callId}`, data),
    initiateCall: (callId) => fetchWrapper.post(`call-center/calls/${callId}/initiate`),
    endCall: (callId) => fetchWrapper.post(`call-center/calls/${callId}/end`),
    bargeIntoCall: (callId) => fetchWrapper.post(`call-center/calls/${callId}/barge-in`),
    listenToCall: (callId) => fetchWrapper.post(`call-center/calls/${callId}/listen`),
    coachToCall: (callId) => fetchWrapper.post(`call-center/calls/${callId}/coach`),
    bulkInitiateCalls: (data) => fetchWrapper.post('call-center/calls/bulk/initiate', data),

    // Call Events
    getCallEvents: (callId, params = {}) => fetchWrapper.get(`call-center/calls/${callId}/events`, params),

    // Transcripts
    getCallTranscripts: (callId, params = {}) => fetchWrapper.get(`call-center/calls/${callId}/transcripts`, params),
    searchTranscripts: (data) => fetchWrapper.post('call-center/transcripts/search', data),

    // Team Members
    getMembers: ({ teams } = {}) => {
        const queryString = buildQueryString({
            teams,
        });
        return fetchWrapper.get(`organizations/5f3569c2647fb5000c019e42/multi/teams/members?${queryString}`);
    },

    // Call Verticals
    getVerticals: (data = {}) => {
        const payload = {
            limit: 100,
            page: 1,
            ...data,
        };
        const queryString = buildQueryString(payload);
        return fetchWrapper.get(`call-center/verticals?${queryString}`);
    },
    getVertical: (id) => {
        return fetchWrapper.get(`call-center/verticals/${id}`);
    },
    createVertical: (data) => {
        return fetchWrapper.post('call-center/verticals', data);
    },
    updateVertical: (id, data) => {
        return fetchWrapper.put(`call-center/verticals/${id}`, data);
    },
    updateVerticalStatus: (id, data) => {
        return fetchWrapper.patch(`call-center/verticals/${id}/status`, data);
    },
    // Call Categories
    getCategories: (data = {}) => {
        const payload = {
            limit: 100,
            page: 1,
            ...data,
        };
        const queryString = buildQueryString(payload);
        return fetchWrapper.get(`call-center/categories?${queryString}`);
    },
};
